import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardContent, Input, Textarea, toast } from '@mlm/ui';
import { apiErrorPayload, getPendingAttempt, gradeAttempt, listPendingAttempts } from '../lib/api';

/**
 * Grading inbox — typed-answer test submissions across every course, oldest
 * first (fair queue). Multiple-choice already auto-scored; the creator awards
 * points per typed answer, and passing mints the learner's certificate on the
 * spot. One shot: a graded attempt cannot be re-graded (the learner retakes
 * instead).
 */
export function GradingPage() {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['creator-grading'],
    queryFn: listPendingAttempts,
  });
  // `?attempt=` arrives from a `creator_grading` bell — open that submission.
  const [params] = useSearchParams();
  const wanted = params.get('attempt');
  const [openId, setOpenId] = useState<string | null>(wanted);
  useEffect(() => {
    if (wanted) setOpenId(wanted);
  }, [wanted]);

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Grading</h1>
        <p className="text-sm text-muted-foreground">
          Test submissions with typed answers waiting for your review. Passing a submission issues
          the learner's certificate immediately (once their lessons are done).
        </p>
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              All caught up — nothing waiting for review.
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => (
                <li key={r.id} className="p-4">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{r.memberName ?? 'Learner'}</span>
                    <span>· {r.courseName}</span>
                    <span>· {r.testTitle}</span>
                    <span>· attempt #{r.attemptNumber}</span>
                    <span>· {new Date(r.submittedAt).toLocaleString()}</span>
                    <Badge variant="outline">
                      auto {r.autoScore}/{r.maxScore}
                    </Badge>
                  </div>
                  {openId === r.id ? (
                    <GradeForm attemptId={r.id} onDone={() => setOpenId(null)} />
                  ) : (
                    <Button className="mt-2" size="sm" onClick={() => setOpenId(r.id)}>
                      Review
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GradeForm({ attemptId, onDone }: { attemptId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { data: attempt } = useQuery({
    queryKey: ['creator-grading', attemptId],
    queryFn: () => getPendingAttempt(attemptId),
  });
  const [grades, setGrades] = useState<Record<string, { points: string; comment: string }>>({});

  const submit = useMutation({
    mutationFn: () =>
      gradeAttempt(
        attemptId,
        attempt!.entries
          .filter((e) => e.kind === 'essay')
          .map((e) => ({
            questionId: e.questionId,
            // Sent verbatim. Clamping here would recreate the silent trim the
            // server now refuses, and hide the disagreement from the grader.
            points: Number(grades[e.questionId]?.points ?? 0),
            comment: grades[e.questionId]?.comment?.trim() || null,
          })),
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['creator-grading'] });
      onDone();
      // The grade can be the last gate on a course, so this is the moment the
      // learner's certificate mints. Saying so is the difference between
      // "graded" and knowing the learner just got their credential.
      if (res.certificateIssued) toast.success('Certificate issued to the learner.');
    },
    // Someone else (another tab, a co-author on the house account) graded it
    // first: the global toast says so, and the stale form must not stay open
    // inviting a second grade the server will refuse again.
    onError: (e) => {
      if (apiErrorPayload(e)?.error === 'already_graded') {
        qc.invalidateQueries({ queryKey: ['creator-grading'] });
        onDone();
      }
    },
    meta: { successMessage: 'Graded.' },
  });

  if (!attempt) return <p className="mt-2 text-sm text-muted-foreground">Loading…</p>;
  const essays = attempt.entries.filter((e) => e.kind === 'essay');
  // An emptied box holds `''`, which is not `undefined`: the old readiness
  // check passed it and posted `Number('') === 0`, scoring the learner zero on
  // a question nobody had actually graded.
  const scored = (questionId: string) => {
    const raw = grades[questionId]?.points;
    return raw !== undefined && raw !== '';
  };
  const overMax = (questionId: string, max: number) =>
    scored(questionId) && Number(grades[questionId]?.points) > max;
  const ready = essays.every((e) => scored(e.questionId) && !overMax(e.questionId, e.points));

  return (
    <div className="mt-3 space-y-3">
      {attempt.entries.map((e, i) => (
        <div key={e.questionId} className="rounded border p-3">
          <p className="text-sm font-medium">
            {i + 1}. {e.prompt}{' '}
            <span className="font-normal text-muted-foreground">
              ({e.points} pt{e.points === 1 ? '' : 's'})
            </span>
          </p>
          {e.kind === 'multiple_choice' ? (
            <p className="mt-1 text-sm">
              Answered: {e.selectedIndex != null ? (e.choices[e.selectedIndex] ?? '—') : '—'}{' '}
              {e.awardedPoints >= e.points ? (
                <Badge className="ml-1" variant="default">
                  correct
                </Badge>
              ) : (
                <Badge className="ml-1" variant="destructive">
                  wrong
                </Badge>
              )}
            </p>
          ) : (
            <>
              <p className="mt-1 whitespace-pre-line rounded bg-muted/50 p-2 text-sm">
                {e.text?.trim() || '— no answer —'}
              </p>
              <div className="mt-2 flex flex-wrap items-start gap-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium">Points (0–{e.points})</label>
                  <Input
                    className="w-full sm:w-24"
                    inputMode="numeric"
                    aria-invalid={overMax(e.questionId, e.points) || undefined}
                    value={grades[e.questionId]?.points ?? ''}
                    onChange={(ev) =>
                      setGrades((g) => ({
                        ...g,
                        [e.questionId]: {
                          points: ev.target.value.replace(/\D/g, ''),
                          comment: g[e.questionId]?.comment ?? '',
                        },
                      }))
                    }
                  />
                  {/* Terse on purpose: this column is `sm:w-24`, and a full
                      sentence here would stretch it and squeeze the feedback box
                      beside it. The label directly above already states the
                      range, so the flag is all that is missing. */}
                  {overMax(e.questionId, e.points) ? (
                    <p className="text-xs text-destructive">Max {e.points}</p>
                  ) : null}
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium">Feedback (optional)</label>
                  <Textarea
                    rows={2}
                    maxLength={2000}
                    value={grades[e.questionId]?.comment ?? ''}
                    onChange={(ev) =>
                      setGrades((g) => ({
                        ...g,
                        [e.questionId]: {
                          points: g[e.questionId]?.points ?? '',
                          comment: ev.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      ))}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
          Submit grade
        </Button>
      </div>
    </div>
  );
}
